import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { resolveMedia } from "../lib/api";
import { Volume2, VolumeX, ChevronRight, Zap } from "lucide-react";

/* ShortsReel - prominent horizontal vertical-video reel for the Home page.
 * Shows the first short auto-playing on the left + a horizontal queue of others.
 * Designed for traffic landing from external links (TikTok/IG/Reels) that expects
 * a vertical-first POV experience.
 */
export default function ShortsReel({ shorts = [] }) {
  const [muted, setMuted] = useState(true);
  const [active, setActive] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  }, [active]);

  if (shorts.length === 0) return null;
  const featured = shorts[active];
  const queue = shorts.filter((_, i) => i !== active).slice(0, 6);

  return (
    <section data-testid="shorts-reel" className="px-4 sm:px-8 lg:px-12 py-8 relative">
      <div className="flex items-end justify-between mb-5">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-[#FFB800]">
            <Zap className="w-3 h-3" /> POV · Reels
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mt-1">Saiyan Shorts</h2>
        </div>
        <Link
          to="/shorts"
          data-testid="reel-open-feed"
          className="aura-button inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-xs uppercase tracking-widest"
        >
          Enter Feed <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Featured vertical autoplay */}
        <div className="md:col-span-4 lg:col-span-3 relative group">
          <Link to={`/watch/${featured.id}`} className="block relative aspect-[9/16] rounded-2xl overflow-hidden bg-black border border-white/10" data-testid={`reel-featured-${featured.id}`}>
            <video
              ref={videoRef}
              src={resolveMedia(featured.playback_url)}
              poster={resolveMedia(featured.thumbnail)}
              muted={muted}
              loop
              playsInline
              autoPlay
              preload="metadata"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30 pointer-events-none" />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-[#FF6B00] text-black text-[10px] font-black uppercase tracking-widest">
              ⚡ Now Playing
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMuted((m) => !m); }}
              data-testid="reel-mute"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:border-[#FFB800]"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="absolute bottom-3 left-3 right-3">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">{featured.category}</span>
              <h3 className="text-base sm:text-lg font-bold text-white leading-tight line-clamp-2 mt-1">{featured.title}</h3>
            </div>
          </Link>
        </div>

        {/* Queue */}
        <div className="md:col-span-8 lg:col-span-9">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {queue.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActive(shorts.findIndex((x) => x.id === s.id))}
                data-testid={`reel-queue-${s.id}`}
                className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-zinc-900 border border-white/5 card-lift text-left"
              >
                {s.thumbnail ? (
                  <img src={resolveMedia(s.thumbnail)} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <video src={resolveMedia(s.playback_url)} muted preload="metadata" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[#FFB800] font-bold">{s.category}</span>
                  <p className="text-xs font-bold text-white leading-tight line-clamp-2">{s.title}</p>
                </div>
              </button>
            ))}
            {Array.from({ length: Math.max(0, 6 - queue.length) }).map((_, i) => (
              <Link
                key={`add-${i}`}
                to="/shorts"
                className="aspect-[9/16] rounded-xl border border-dashed border-white/10 flex items-center justify-center text-zinc-600 hover:border-[#FF6B00]/40 hover:text-[#FFB800] transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
